import { useState, useEffect } from 'react';

import gql from 'graphql-tag';
import { useParams } from 'react-router-dom';

import { useLazyQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import {
  PublicUserFragment,
  FullProfileWithThemeFragment,
  AddressFragment,
  SectionFragment,
  ThemeFragment,
  ChannelSettingsRequirementsFragment,
} from 'lane-shared/graphql/fragments';
import { useFlag, useMultiLanguage } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { AddressFragmentType } from 'lane-shared/graphql/fragments/AddressFragment';
import { ChannelSettingsRequirementsFragmentType } from 'lane-shared/graphql/fragments/ChannelSettingsRequirementsFragment';
import { FullProfileWithThemeFragmentType } from 'lane-shared/graphql/fragments/FullProfileWithThemeFragment';
import { PublicUserFragmentType } from 'lane-shared/graphql/fragments/PublicUserFragment';
import { SectionFragmentType } from 'lane-shared/graphql/fragments/SectionFragment';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { isProbablyUUID } from 'uuid-encoding';
import { ChannelSettingsType, Channel } from 'lane-shared/types/ChannelType';
import { GroupRoleJoinRuleType } from 'lane-shared/types/GroupRoleJoinRuleType';
import { RequirementsSource } from 'lane-shared/types/features/RequirementsFeatureProperties';
import { ModuleDefinition } from 'lane-shared/types/modules/moduleDefinition';
import { Maybe, WhitelabelChannel } from 'graphql-query-contracts';

export type AdminChannelData = Pick<
  Channel,
  | '_id'
  | '_created'
  | '_updated'
  | 'name'
  | 'website'
  | 'description'
  | 'slug'
  | 'type'
  | 'experienceType'
  | 'isPublic'
  | 'isDiscoverable'
  | 'isCustomer'
  | 'isSub'
  | 'inviteOnly'
  | 'filters'
  | 'stats'
  | 'tags'
  | 'channelModules'
> & {
  _createdBy: PublicUserFragmentType;
  _updatedBy: PublicUserFragmentType;
  specialPermissions: string[];
  whitelabels: Maybe<Array<WhitelabelChannel>>;
  settings: Pick<
    ChannelSettingsType,
    | 'maxUsers'
    | 'hasPerksEnabled'
    | 'areUserTemplatesAllowed'
    | 'subscribersJoinParent'
    | 'subscribersAreDecoyed'
    | 'hasBillingPaymentsEnabled'
    | 'hasWorkOrdersEnabled'
    | 'hasWorkOrderServiceRequestsEnabled'
    | 'hasWorkOrderPreventiveMaintenanceEnabled'
    | 'hasWorkOrderEquipmentEnabled'
    | 'hasWorkOrderCrossPropertyEnabled'
    | 'hasVisitorManagementEnabled'
    | 'hasHardwareManagementEnabled'
    | 'hasHardwarePinManagementEnabled'
    | 'hasReservableManagementEnabled'
    | 'hasSurveysEnabled'
    | 'hasAccessControlEnabled'
    | 'multiLanguageEnabled'
    | 'channelLanguages'
    | 'enabledLanguagesForMembers'
    | 'hasMessagingAndMarketplaceEnabled'
    | 'hasSmartUserManagementEnabled'
    | 'hasInspectionsEnabled'
  > & {
    groupRoleJoinRules: Pick<
      GroupRoleJoinRuleType,
      '_id' | 'groupRoleId' | 'joinGroupRoleId' | 'channelId'
    >[];
    requirements: ChannelSettingsRequirementsFragmentType;
    effectiveParentRequirements: ChannelSettingsRequirementsFragmentType & {
      source: Pick<RequirementsSource, 'slug' | 'profileName' | '_id'>;
    };
  };
  address: AddressFragmentType;
  profile: FullProfileWithThemeFragmentType;
  sections: SectionFragmentType[];
  parent: Pick<Channel, '_id' | 'name' | 'slug' | 'type'>;
  channelModules: ModuleDefinition[];
};

export type UseChannelForAdminQueryResponse = {
  channel: AdminChannelData;
};

type UseChannelForAdminSecuredQueryResponse = {
  channelForAdmin: AdminChannelData;
};

type AdminChannelQueryResponse =
  | UseChannelForAdminQueryResponse
  | UseChannelForAdminSecuredQueryResponse;

const AdminChannelFieldsFragment = gql`
  ${PublicUserFragment}
  ${ThemeFragment}
  ${FullProfileWithThemeFragment}
  ${AddressFragment}
  ${SectionFragment}
  ${ChannelSettingsRequirementsFragment}

  fragment AdminChannelFields on Channel {
    _id
    _created
    _updated
    _createdBy {
      ...PublicUserFragment
    }
    _updatedBy {
      ...PublicUserFragment
    }

    name
    website
    description
    slug
    type
    experienceType
    isPublic
    isDiscoverable
    isCustomer
    isSub
    isLive
    hasCustomOffboarding
    inviteOnly
    restrictPropertyAccess
    filters
    specialPermissions
    tags

    property {
      id
    }

    whitelabels {
      whitelabel {
        url
      }
    }

    settings {
      maxUsers
      offboardingDelayDays
      hasPerksEnabled
      areUserTemplatesAllowed
      subscribersJoinParent
      subscribersAreDecoyed
      hasReservableManagementEnabled
      hasSurveysEnabled
      hasWorkOrdersEnabled
      hasWorkOrderServiceRequestsEnabled
      hasWorkOrderPreventiveMaintenanceEnabled
      hasWorkOrderEquipmentEnabled
      hasWorkOrderCrossPropertyEnabled
      hasBillingPaymentsEnabled
      hasVisitorManagementEnabled
      hasHardwareManagementEnabled
      hasHardwarePinManagementEnabled
      hasAccessControlEnabled
      hasMessagingAndMarketplaceEnabled
      hasSmartUserManagementEnabled
      hasInspectionsEnabled
      multiLanguageEnabled
      enabledLanguagesForMembers
      channelLanguages
      groupRoleJoinRules {
        _id
        groupRoleId
        joinGroupRoleId
        channelId
      }
      requirements {
        ...ChannelSettingsRequirementsFragment
      }
      effectiveParentRequirements {
        source {
          slug
          profileName
          _id
        }
        ...ChannelSettingsRequirementsFragment
      }
    }

    stats {
      sf
      subscribers
    }

    address {
      ...AddressFragment
    }

    profile {
      ...FullProfileWithThemeFragment
    }

    parent {
      _id
      name
      slug
      type
    }

    sections {
      ...SectionFragment
    }

    channelModules {
      name
      friendlyName
      _id
      disabled
      dataDefinition
      settings
    }
  }
`;

const channelQuery = gql`
  ${AdminChannelFieldsFragment}

  query getChannelForAdmin($id: UUID, $slug: String) {
    channel(_id: $id, slug: $slug) {
      ...AdminChannelFields
    }
  }
`;

const channelForAdminQuery = gql`
  ${AdminChannelFieldsFragment}

  query getChannelForAdmin($id: UUID, $slug: String) {
    channelForAdmin(_id: $id, slug: $slug) {
      ...AdminChannelFields
    }
  }
`;

export default function useChannelForAdminQuery() {
  const { id: channelId } = useParams<{ id: string }>();
  const [timeZone, setTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const { translate } = useMultiLanguage();

  const isChannelForAdminQueryEnabled = useFlag(
    FeatureFlag.ChannelForAdminQuery,
    false
  );

  const activeQuery = isChannelForAdminQueryEnabled
    ? channelForAdminQuery
    : channelQuery;

  const [fetchChannel, channelResults] =
    useLazyQuery<AdminChannelQueryResponse>(activeQuery, {
      client: getClient(),
      fetchPolicy: 'no-cache',
    });

  const channelData = channelResults?.data;
  const channel =
    channelData &&
    ('channelForAdmin' in channelData
      ? channelData.channelForAdmin
      : channelData.channel);
  const specialPermissions = channel?.specialPermissions || [];

  function refetchChannel() {
    if (channelId) {
      let slug;
      let id;

      // may be a slug or a id passed in
      if (isProbablyUUID(channelId)) {
        id = channelId;
      } else {
        slug = channelId;
      }

      if (!channelResults.loading) {
        fetchChannel({
          variables: {
            id,
            slug,
          },
        });
      }
    }
  }

  useEffect(() => {
    refetchChannel();
  }, [channelId]);

  useEffect(() => {
    if (channel?.address?.geo) {
      // set the timeZone to the channel instead of the users time zone.
      const [longitude, latitude] = channel.address.geo;

      setTimeZone(getTimeZoneByGeoLocation({ longitude, latitude }));
    }
  }, [channel?.address]);

  const translatedChannelSections = translate({
    model: channel?.sections?.map(section => ({
      ...section,
      channel: {
        _id: channel?._id,
        settings: {
          ...channel?.settings,
        },
      },
    })),
    columns: ['name'],
  });

  const translatedChannel = channel && {
    ...channel,
    sections: translatedChannelSections,
  };

  return {
    channel: translatedChannel,
    timeZone,
    specialPermissions,
    loading: channelResults.loading,
    error: channelResults.error,
    called: channelResults.called,
    refetchChannel,
  };
}
